import { supabase } from '../supabaseClient';

// Register a new user
export const register = async (userData) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          full_name: userData.fullName || '',
        }
      }
    });
    
    if (error) throw error;
    
    // Create profile record if signup succeeded
    if (data.user) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([
          {
            id: data.user.id,
            email: userData.email,
            full_name: userData.fullName || '',
            created_at: new Date().toISOString()
          }
        ]);
        
      if (profileError) throw profileError;
    }
    
    return data;
  } catch (error) {
    throw error.message || 'Registration failed';
  }
};

// Login user
export const login = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    return data;
  } catch (error) {
    throw error.message || 'Login failed';
  }
};

// Verify email with code
export const verifyEmail = async (token) => {
  try {
    // Supabase handles email verification automatically
    // This can be used for custom verification flows if needed
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'email'
    });
    
    if (error) throw error;
    return { message: 'Email verified successfully' };
  } catch (error) {
    throw error.message || 'Email verification failed';
  }
};

// Resend verification code
export const resendVerificationCode = async (email) => {
  try {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email
    });
    
    if (error) throw error;
    return { message: 'Verification email sent' };
  } catch (error) {
    throw error.message || 'Failed to resend verification code';
  }
};

// Update user profile
export const updateProfile = async (profileData) => {
  try {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('User not authenticated');
    
    // Map frontend keys to Supabase column names
    const updatePayload = {
      first_name: profileData.firstName,
      last_name: profileData.lastName,
      company_name: profileData.companyName,
      phone_number: profileData.phoneNumber,
      street: profileData.street,
      city: profileData.city,
      state: profileData.state,
      zip_code: profileData.zipCode,
      country: profileData.country,
    };

    const { error } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('id', user.id);
      
    if (error) throw error;
    
    return { message: 'Profile updated successfully' };
  } catch (error) {
    throw error.message || 'Failed to update profile';
  }
};
// Change password with current password verification
export const changePassword = async (currentPassword, newPassword) => {
  try {
    // Step 1: Get the user's current email to re-authenticate
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) throw new Error('User not found');
    
    // Step 2: Verify the current password by attempting to sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword
    });
    
    if (signInError) throw new Error('Current password is incorrect');
    
    // Step 3: If sign-in succeeded, update to the new password
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (updateError) throw updateError;
    
    return { message: 'Password changed successfully' };
  } catch (error) {
    throw error.message || 'Failed to change password';
  }
};

// Check if user is authenticated
export const isAuthenticated = async () => {
  const { data } = await supabase.auth.getSession();
  return !!data.session;
};

// Get current user information
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    
    if (error) throw error;
    
    if (data.user) {
      // Get additional profile information
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', data.user.id)
        .single();
        
      if (profileError) throw profileError;
      
      // Combine auth user and profile data
      return {
        ...data.user,
        profile: profileData
      };
    }
    
    return null;
  } catch (error) {
    throw error.message || 'Failed to get user information';
  }
};

// Logout user
export const logout = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return true;
  } catch (error) {
    throw error.message || 'Failed to logout';
  }
};